// frontend/src/utils/index.ts - UPDATED for new API, hooks, and store compatibility

import { type ClassValue, clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';

// ===== UTILITY FUNCTIONS =====

/**
 * Enhanced className merging utility for Tailwind CSS
 */
export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

// ===== DATE AND TIME UTILITIES =====

/**
 * Format date with enhanced error handling and null checking
 */
export const formatDate = (date: string | Date | null | undefined, includeTime = true): string => {
  if (!date) return 'N/A';

  try {
    const d = typeof date === 'string' ? new Date(date) : date;

    if (isNaN(d.getTime())) return 'Invalid Date';

    const day = String(d.getDate()).padStart(2, '0');
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const month = months[d.getMonth()];
    const year = d.getFullYear();

    if (!includeTime) {
      return `${day}-${month}-${year}`;
    }

    const hours = String(d.getHours()).padStart(2, '0');
    const minutes = String(d.getMinutes()).padStart(2, '0');
    return `${day}-${month}-${year}, ${hours}:${minutes}`;
  } catch (error) {
    console.warn('Error formatting date:', error);
    return 'Invalid Date';
  }
};

/**
 * Format time only with error handling
 */
export const formatTime = (date: string | Date | null | undefined): string => {
  if (!date) return 'N/A';
  
  try {
    const d = typeof date === 'string' ? new Date(date) : date;
    
    if (isNaN(d.getTime())) return 'Invalid Time';
    
    return d.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch (error) {
    console.warn('Error formatting time:', error);
    return 'Invalid Time';
  }
};

/**
 * Format date relative to now (e.g., "2 hours ago", "yesterday")
 */
export const formatRelativeTime = (date: string | Date | null | undefined): string => {
  if (!date) return 'Unknown';
  
  try {
    const now = new Date();
    const then = new Date(date);
    
    if (isNaN(then.getTime())) return 'Invalid date';
    
    const diffInSeconds = Math.floor((now.getTime() - then.getTime()) / 1000);
    
    if (diffInSeconds < 60) return 'just now';
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)} minutes ago`;
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)} hours ago`;
    if (diffInSeconds < 2592000) return `${Math.floor(diffInSeconds / 86400)} days ago`;
    
    return formatDate(then, false);
  } catch (error) {
    console.warn('Error formatting relative time:', error);
    return 'Unknown';
  }
};

// ===== TEXT UTILITIES =====

/**
 * Truncate text with proper null/undefined checking
 */
export const truncateText = (text: string | null | undefined, maxLength: number): string => {
  if (!text || typeof text !== 'string') {
    return '';
  }
  
  if (text.length <= maxLength) return text;
  return text.substring(0, maxLength) + '...';
};

/**
 * Capitalize first letter of each word
 */
export const capitalizeWords = (text: string | null | undefined): string => {
  if (!text || typeof text !== 'string') return '';
  
  return text.toLowerCase().replace(/\b\w/g, (char) => char.toUpperCase());
};

/**
 * Convert camelCase or PascalCase to readable text
 */
export const humanizeText = (text: string): string => {
  if (!text) return '';
  
  return text
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, (str) => str.toUpperCase())
    .trim();
};

// ===== VALIDATION UTILITIES =====

/**
 * MongoDB ObjectId validation - Essential for API compatibility
 */
export const isValidObjectId = (id: string | null | undefined): boolean => {
  if (!id || typeof id !== 'string') return false;
  return /^[0-9a-fA-F]{24}$/.test(id);
};

/**
 * Validate multiple ObjectIds
 */
export const areValidObjectIds = (ids: string[] | null | undefined): boolean => {
  if (!ids || !Array.isArray(ids)) return false;
  return ids.every(id => isValidObjectId(id));
};

/**
 * Email validation with enhanced regex
 */
export const isValidEmail = (email: string | null | undefined): boolean => {
  if (!email || typeof email !== 'string') return false;
  
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email.trim());
};

/**
 * URL validation
 */
export const isValidUrl = (url: string | null | undefined): boolean => {
  if (!url || typeof url !== 'string') return false;
  
  try {
    new URL(url);
    return true;
  } catch {
    return false;
  }
};

// ===== ARRAY AND OBJECT UTILITIES =====

/**
 * Ensure array is properly initialized and not undefined
 */
export const ensureArray = <T>(value: T[] | null | undefined): T[] => {
  return Array.isArray(value) ? value : [];
};

/**
 * Safe array access helper with optional default value
 */
export const safeArrayAccess = <T>(
  arr: T[] | null | undefined, 
  index: number, 
  defaultValue?: T
): T | undefined => {
  if (!Array.isArray(arr) || index < 0 || index >= arr.length) {
    return defaultValue;
  }
  return arr[index];
};

/**
 * Extract ID from object or string (compatible with both _id and id fields)
 */
export const extractId = (item: string | { _id?: string; id?: string } | null | undefined): string | undefined => {
  if (typeof item === 'string') return item;
  if (!item || typeof item !== 'object') return undefined;
  return item._id || item.id;
};

/**
 * Deep clone object safely
 */
export const deepClone = <T>(obj: T): T => {
  if (obj === null || typeof obj !== 'object') return obj;
  if (obj instanceof Date) return new Date(obj.getTime()) as T;
  if (obj instanceof Array) return obj.map(deepClone) as T;
  
  const cloned = {} as T;
  for (const key in obj) {
    if (obj.hasOwnProperty(key)) {
      cloned[key] = deepClone(obj[key]);
    }
  }
  return cloned;
};

// ===== FILE AND SIZE UTILITIES =====

/**
 * Format file size in human readable format
 */
export const formatFileSize = (bytes: number | null | undefined): string => {
  if (!bytes || bytes === 0) return '0 Bytes';
  
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

/**
 * Get file extension from filename
 */
export const getFileExtension = (filename: string | null | undefined): string => {
  if (!filename || typeof filename !== 'string') return '';
  
  const lastDot = filename.lastIndexOf('.');
  return lastDot === -1 ? '' : filename.substring(lastDot + 1).toLowerCase();
};

/**
 * Check if file type is allowed
 */
export const isAllowedFileType = (filename: string, allowedTypes: string[]): boolean => {
  if (!filename || !Array.isArray(allowedTypes) || allowedTypes.length === 0) return false;
  
  const extension = getFileExtension(filename);
  return allowedTypes.includes(extension);
};

// ===== ERROR HANDLING UTILITIES =====

/**
 * Enhanced error message formatting for user-friendly display
 */
export const formatErrorMessage = (error: any): string => {
  if (!error) return 'An unknown error occurred';
  
  // Handle string errors
  if (typeof error === 'string') return error;
  
  // Handle Error objects
  if (error instanceof Error) {
    const message = error.message || 'An error occurred';
    
    // Enhanced backend error handling
    if (message.includes('Network Error') || message.includes('fetch')) {
      return 'Network connection failed. Please check your internet connection and try again.';
    }
    
    if (message.includes('timeout')) {
      return 'Request timed out. Please try refreshing the page or contact your administrator.';
    }
    
    if (message.includes('Cannot read properties of undefined')) {
      return 'Backend data error: Some required data is missing or corrupted. This may require administrator attention.';
    }
    
    if (message.includes('CastError') || message.includes('Cast to ObjectId failed')) {
      return 'Invalid ID format: The provided ID is not in the correct format. Please try again or contact support.';
    }
    
    if (message.includes('ValidationError')) {
      return 'Data validation failed: The provided information does not meet the required format. Please check your input and try again.';
    }
    
    if (message.includes('duplicate key error') || message.includes('E11000')) {
      return 'Duplicate entry: This operation would create a duplicate record that already exists.';
    }
    
    return message;
  }
  
  // Handle API response errors
  if (error.response?.data?.message) {
    return formatErrorMessage(error.response.data.message);
  }
  
  if (error.message) {
    return formatErrorMessage(error.message);
  }
  
  return 'An unexpected error occurred';
};

/**
 * Check if error is a network error
 */
export const isNetworkError = (error: any): boolean => {
  if (!error) return false;
  
  const message = error.message || error.toString();
  return message.includes('Network Error') || 
         message.includes('fetch') || 
         message.includes('timeout') ||
         error.code === 'NETWORK_ERROR';
};

// ===== DEBOUNCE AND THROTTLE UTILITIES =====

/**
 * Debounce function for search inputs and frequent API calls
 */
export const debounce = <T extends (...args: any[]) => any>(
  func: T,
  wait: number
): ((...args: Parameters<T>) => void) => {
  let timeout: NodeJS.Timeout;
  
  return (...args: Parameters<T>) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  };
};

/**
 * Throttle function for scroll handlers and frequent events
 */
export const throttle = <T extends (...args: any[]) => any>(
  func: T,
  limit: number
): ((...args: Parameters<T>) => void) => {
  let inThrottle: boolean;
  
  return (...args: Parameters<T>) => {
    if (!inThrottle) {
      func(...args);
      inThrottle = true;
      setTimeout(() => inThrottle = false, limit);
    }
  };
};

// ===== SEARCH AND FILTER UTILITIES =====

/**
 * Highlight search terms in text
 */
export const highlightSearchTerms = (text: string, searchTerm: string): string => {
  if (!text || !searchTerm) return text;
  
  const regex = new RegExp(`(${searchTerm})`, 'gi');
  return text.replace(regex, '<mark>$1</mark>');
};

/**
 * Filter array by search term across multiple fields
 */
export const filterBySearchTerm = <T>(
  items: T[], 
  searchTerm: string, 
  searchFields: (keyof T)[]
): T[] => {
  if (!searchTerm || !searchTerm.trim()) return items;
  
  const term = searchTerm.toLowerCase().trim();
  
  return items.filter(item => 
    searchFields.some(field => {
      const value = item[field];
      if (typeof value === 'string') {
        return value.toLowerCase().includes(term);
      }
      if (typeof value === 'number') {
        return value.toString().includes(term);
      }
      return false;
    })
  );
};

// ===== COLOR AND STYLING UTILITIES =====

/**
 * Get status color class based on status value
 */
export const getStatusColor = (status: string | null | undefined): string => {
  if (!status) return 'bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-300';
  
  const statusLower = status.toLowerCase();
  
  if (['active', 'approved', 'completed', 'success'].includes(statusLower)) {
    return 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-300';
  }
  
  if (['pending', 'draft', 'in_progress', 'processing'].includes(statusLower)) {
    return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-300';
  }
  
  if (['rejected', 'failed', 'error', 'cancelled'].includes(statusLower)) {
    return 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-300';
  }
  
  if (['disabled', 'inactive', 'archived'].includes(statusLower)) {
    return 'bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-300';
  }
  
  return 'bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-300';
};

/**
 * Get priority color class
 */
export const getPriorityColor = (priority: string | null | undefined): string => {
  if (!priority) return 'bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-300';
  
  switch (priority.toLowerCase()) {
    case 'urgent':
    case 'high':
      return 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-300';
    case 'medium':
    case 'normal':
      return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-300';
    case 'low':
      return 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-300';
    default:
      return 'bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-300';
  }
};

// ===== VALIDATION HELPERS AVAILABLE IN SEPARATE FILE =====
// Import from './validationHelpers' for specialized business logic validations

// ===== RE-EXPORT IMPORTANT CONSTANTS =====
export const API_CONSTANTS = {
  DEFAULT_PAGE_SIZE: 10,
  MAX_PAGE_SIZE: 100,
  DEFAULT_RETRY_COUNT: 2,
  DEFAULT_RETRY_DELAY: 1000,
  DEFAULT_DEBOUNCE: 300,
  CACHE_TTL: 300000, // 5 minutes
  RATE_LIMIT_WINDOW: 60000 // 1 minute
} as const;

export const SUPPORTED_FILE_TYPES = {
  DOCUMENTS: ['pdf', 'doc', 'docx', 'txt', 'rtf'],
  IMAGES: ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'svg'],
  SPREADSHEETS: ['xls', 'xlsx', 'csv'],
  PRESENTATIONS: ['ppt', 'pptx'],
  ARCHIVES: ['zip', 'rar', '7z', 'tar', 'gz']
} as const;

export const MAX_FILE_SIZES = {
  DOCUMENT: 50 * 1024 * 1024, // 50MB
  IMAGE: 10 * 1024 * 1024,    // 10MB
  ARCHIVE: 100 * 1024 * 1024, // 100MB
  DEFAULT: 25 * 1024 * 1024   // 25MB
} as const;